import { api } from '@/api/client';

export interface SettlementFilters {
  settlement_id?: string;
  settlement_status?: string;
  merchant_uuid?: string;
  page?: number;
  page_size?: number;
}

export interface SettledTransactionItem {
  transaction_id: string;
  transaction_amount: string | number;
  service_charge: string | number;
  service_tax: string | number;
}

export interface SettlementItem {
  settlement_id: string;
  merchant_name: string;
  total_transaction_amount: string | number;
  settlement_amount: string | number;
  settlement_charges: string | number;
  settlement_date: string;
  is_settled: string | boolean;
  utr_number: string;
  payout_date_time: string;
  payout_amount: string | number;
  settled_transactions: SettledTransactionItem[];
}

export interface SettlementListResponse {
  success: boolean;
  data: SettlementItem[];
  count?: number;
}

export interface SettlementStatsResponse {
  success: boolean;
  data: {
    total_settled_amount: string | number;
    total_unsettled_amount: string | number;
    pending_settlements: string | number;
    total_settlements: string | number;
  };
}

export interface SettlementDownloadResponse {
  success: boolean;
  download_url?: string;
  message?: string;
}

export interface MerchantItem {
  merchant_uuid: string;
  merchant_name: string;
}

const SETTLEMENT_ENDPOINTS = {
  LIST: '/backoffice/api/v1/settlements',
  FILTER: '/backoffice/api/v1/settlements',
  STATS: '/backoffice/api/v1/settlements-stats',
  DOWNLOAD: '/backoffice/api/v1/settlements/download',
  DETAIL: (id: string) => `/backoffice/api/v1/settlements/${id}/`,
} as const;

const MERCHANT_ENDPOINTS = {
  LIST: '/backoffice/api/v1/merchants/',
} as const;

class SettlementListService {

  /**
   * Get paginated list of settlements
   */
  async getSettlements(page: number = 1, pageSize: number = 50): Promise<any> {
    try {
      const url = `${SETTLEMENT_ENDPOINTS.LIST}`;
      const response = await api.get<any>(url, {
        params: {
          page: page,
          page_size: pageSize,
        },
      });

      return response;

    } catch (error) {
      throw this.handleError(error, 'Failed to fetch transactions');
    }
  }

  /**
   * Get transaction statistics
   */
  async getSettlementsStats(): Promise<any> {
    try {
      const response = await api.get<any>(SETTLEMENT_ENDPOINTS.STATS);
      return response;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch transaction statistics');
    }
  }

  /**
   * Get merchants for dropdown
   */
  async getMerchants(page: number = 1, pageSize: number = 1000): Promise<any> {
    try {
      const response = await api.get<any>(MERCHANT_ENDPOINTS.LIST, {
        params: {
          page: page,
          page_size: pageSize,
        },
      });
      return response;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch merchants');
    }
  }

  /**
     * Get single transaction by ID
     */
  async getSettlementData(id?: string, merchantTransactionId?: string): Promise<any> {
    try {
      if (!id && !merchantTransactionId) {
        throw new Error('Either settlement_id or settlement_uuid must be provided');
      }

      const queryParams = new URLSearchParams();

      if (id) {
        queryParams.append('settlement_id', id);
      }

      if (merchantTransactionId) {
        queryParams.append('settlement_uuid', merchantTransactionId);
      }

      const url = `${SETTLEMENT_ENDPOINTS.FILTER}?${queryParams.toString()}`;
      const response = await api.get<{ data: any }>(url);
      return response;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch transaction');
    }
  }


  /**
 * Get single settlements by ID
 */
  async getSettlementStatusData(status?: string): Promise<any> {
    try {
      if (!status) {
        throw new Error('Either status must be provided');
      }

      const queryParams = new URLSearchParams();

      if (status) {
        queryParams.append('settlement_status', status);
      }

      const url = `${SETTLEMENT_ENDPOINTS.FILTER}?${queryParams.toString()}`;
      const response = await api.get<{ data: any }>(url);
      return response;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch transaction');
    }
  }

  /**
   * Get settlements filtered by merchant UUID
   */
  async getSettlementsByMerchant(merchantUuid: string): Promise<any> {
    try {
      if (!merchantUuid) {
        throw new Error('merchant_uuid must be provided');
      }

      const queryParams = new URLSearchParams();
      queryParams.append('merchant_uuid', merchantUuid);

      const url = `${SETTLEMENT_ENDPOINTS.FILTER}?${queryParams.toString()}`;
      const response = await api.get<{ data: any }>(url);
      return response;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch settlements by merchant');
    }
  }

  async downloadSettlement(settlementId?: string): Promise<SettlementDownloadResponse> {
    const response = await api.post<SettlementDownloadResponse>('/merchant/v1/download-settlement/', {
      settlement_id: settlementId,
    });
    return response;
  }

  /**
 * Handle API errors
 */
  private handleError(error: unknown, defaultMessage: string): Error {
    if (error && typeof error === 'object' && 'message' in error) {
      return error as Error;
    }
    return new Error(defaultMessage);
  }
}

// Create singleton instance
export const settlementListService = new SettlementListService();

// Export for testing
export { SettlementListService }


